import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {motion} from "framer-motion";
import {Image} from "primereact/image";
import {Apple, Facebook, GitHub, Google} from "@mui/icons-material";
import Error from "@/components/shared/error";
import React, {useEffect, useState} from "react";
import {useRouter} from "next/navigation";
import {useTranslation} from "react-i18next";
import {ERROR_PROPS} from "@/components/shared/type";
import {FIELDS} from "@/components/admin/login/types";
import {useResetPasswordMutation} from "@/redux/rtk/login/login";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateGlobalLoading} from "@/redux/slices/settings";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomSnackbar from "@/components/snackbar/customSnackbar";
import {defaultLoginBackgroundImage} from "@/components/constants";

const button = {
    borderRadius: 2,
    py: 1.5,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 600,
    boxShadow: "0 8px 16px rgba(0, 13, 255, 0.2)"
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword = ({setStep, fields, setFields, isAdmin = false}: FIELDS) => {
    const {theme: selectedTheme, webContext, siteImages} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const theme = settingsSelectors.getTheme(state);
        const siteImages = settingsSelectors.getSiteImages(state);
        return {webContext, theme, siteImages};
    }, shallowEqual);
    const dispatch = useDispatch();
    const router = useRouter();
    const theme = useTheme();
    const {t} = useTranslation("login");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [err, setErr] = useState<ERROR_PROPS | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [rem, setRem] = useState<number>(fields?.duration ?? 0);
    let timeoutId: NodeJS.Timeout;

    const [resetPassword, {data, error, isLoading: loading}] = useResetPasswordMutation();


    const setDuration = (seconds: number, ind: number = 0) => {
        clearInterval(timeoutId);
        timeoutId = setInterval(() => {
            if ((seconds - ind) > 0) {
                setRem(seconds - ind);
                ind++;
            } else {
                setRem(0);
                clearInterval(timeoutId);
            }
        }, 1000);
    }

    const handleData = (data: any) => {
        const {code, redirectTo, data: nestedData} = data?.data || {};
        const {activationCodeTimeDuration = 0, appUrl, defaultRedirection} = nestedData || {};
        if (code === 200) {
            if (redirectTo) {
                dispatch(updateGlobalLoading(true));
                router.replace(redirectTo);

            } else {
                if (activationCodeTimeDuration > 0) {
                    setFields!({
                        ...fields,
                        duration: activationCodeTimeDuration
                    });
                }
                setStep!(3)
            }
        }
    };

    useEffect(() => {
        handleData(data)
        return () => {
            clearInterval(timeoutId)
        };
    }, [data]);

    useEffect(() => {
        // @ts-ignore
        if (error?.localesData?.rem) setDuration(error.localesData.rem)
    }, [error]);

    useEffect(() => {
        clearInterval(timeoutId);
        if (fields?.duration > 0) {
            setDuration(fields.duration!)
        }
        return () => {
            clearInterval(timeoutId)
        }
    }, [fields]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFields!({
            email: e.target.value
        });
        setIsError(false)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!fields?.email || !emailRegex.test(fields.email!)) {
            setIsError(true);
            return;
        }
        void resetPassword({
            email: fields.email,
            textKeys: JSON.stringify({
                passwordResetCode: t("passwordResetCode")
            })
        })
    }

    const handleClose = () => {
        setSuccessMessage("")
    }

    return <Grid container className="df jcc aic we1 mhe1 hfc">

        <CustomSnackbar open={!!successMessage} handleClose={handleClose}
                        message={successMessage}/>

        <Grid
            size={{xs: false, md: 6, lg: 8}}
            className="df jcc aic"
            sx={{
                alignSelf: "stretch",
                ...!isMobile ? {
                    backgroundImage: siteImages?.images?.[`${isAdmin ? 'loginImageAdmin' : 'loginImage'}`] ? `url(${siteImages.dir}${siteImages.images[`${isAdmin ? 'loginImageAdmin' : 'loginImage'}`]}1.webp)` : defaultLoginBackgroundImage,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: (theme) => theme.palette?.backgrounds?.secondary
                } : {}
            }}
        >
        </Grid>
        <Grid className="jcc aic df" size={{xs: 12, md: 6, lg: 4}}
               sx={{background: theme => theme.palette?.backgrounds?.primary}}>

            <Grid container sx={{
                width: {
                    xs: "100%",
                    sm: "30rem",
                },
                background: theme => theme.palette?.backgrounds?.primary
            }}>
                <Box
                    className="he1 df fdc aic jcc we1"
                    sx={{
                        borderRadius: "10px",
                        p: 4
                    }}
                >
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                        style={{width: "100%"}}
                    >
                        {(webContext?.logo_1_url && webContext?.logo_2_url) ?
                            <Box className="df jcc">
                                <Image
                                    src={selectedTheme === 1 ? `${webContext.logo_2_url}1.webp` : `${webContext.logo_1_url}1.webp`}
                                    alt="Company Logo"
                                    width={"150px"}
                                />
                            </Box> : null}
                        <Box sx={{mb: 5, mt: 4}}>
                            <Typography
                                variant="h4"
                                component="h1"
                                fontWeight="bold"
                                sx={{color: (theme: Theme) => theme.palette?.texts?.main, mb: 1, mt: 2}}
                            >
                                {t('forgotPassword')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('forgotPasswordText')}
                            </Typography>
                        </Box>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>

                                <TextField
                                    fullWidth
                                    label={t('emailAddress')}
                                    name="email"
                                    type="email"
                                    variant="outlined"
                                    value={fields.email}
                                    onChange={handleChange}
                                    error={isError}
                                    helperText={!isError ? null : t('emailIsInvalid')}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                    }}
                                />

                                <Grid container>
                                    <Grid size={{xs: 12, md: 6}} sx={{p: 0.5}}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={loading || !fields.email || !emailRegex.test(fields.email!)}
                                            sx={{
                                                ...button
                                            }}
                                            onClick={() => setStep!(3)}
                                        >
                                            {t('enterCode')}
                                        </Button>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 6}} sx={{p: 0.5}}>
                                        <LoadingButton
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={rem > 0 || !fields.email || !emailRegex.test(fields.email!)}
                                            loading={loading}
                                            sx={{
                                                ...button
                                            }}
                                        >
                                            {rem > 0 ? `${t('activate.resendIn', {rem})}` : t('sendCode')}
                                        </LoadingButton>
                                    </Grid>
                                </Grid>

                            </Stack>
                        </form>

                        {(!!error || !!err) && <Error error={(error || err) as ERROR_PROPS} timeout={10000}/>}

                        <Grid container className="df jcc" sx={{mt: 2, fontSize: "1.1rem"}}>
                            <Grid>{t('alreadyHaveAnAccount')}</Grid>
                            <Grid
                                className="fwb cp"
                                sx={{ml: 0.5, mr: 0.5, color: "primary.main"}}
                                onClick={() => setStep!(1)}
                            >
                                {t('signIn')}
                            </Grid>
                        </Grid>

                    </motion.div>
                </Box>
            </Grid>

        </Grid>
    </Grid>
}

export default ForgotPassword;